/**
 * Dm input currency component
 */

import {
  AbstractType,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  InjectionToken,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Type,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl, ValidatorFn } from '@angular/forms';
import {
  first,
  get,
  gt,
  has,
  isEmpty,
  isFunction,
  isInteger,
  isNil,
  isNumber,
  merge,
} from 'lodash';
import {
  CountryFormatService,
  NumberFormatService,
  ScanningService,
  StateService,
} from '@shared/services';
import { XVIInputNumberComponent } from '../input-number/input-number.component';
import { ValidationService } from '../services/validation/validation.service';
import {
  DEFAULT_ARTIFICIAL_INPUT_THRESHOLD,
  DEFAULT_INPUT_THOUSANDS_DIVIDER,
  DEFAULT_NUMBER_PRECISION,
} from '@config/global-constants';
import {
  observableCombineLatest,
  rx,
  Subject,
} from '@app/shared/rx/rx-wrapper';
import { floatNumberRoundingFormat } from '@app/shared/utils/rounding-utils';
import {
  getDecimalPlaces,
  removeInvalidZeros,
} from '@app/shared/utils/number-utils';
import { IFieldPreferences } from '../services/schema-form/schema-form-interfaces';
import { EInputEvent, IArtificialInputWatcherOption } from '@app/shared/types';
import { artificialInputWatcher, numberCheckValid } from '@app/shared/utils';
import { lazyInject } from '@app/containers/base/decorators/lazy-inject.decorator';
import { generateDecimalRegexp } from '@app/shared/constants';
import { decimalNumber } from '@app/shared/common';

@Component({
  selector: 'xvi-input-currency',
  templateUrl: './input-currency.component.html',
  styleUrls: ['./input-currency.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XVIInputCurrencyComponent
  extends XVIInputNumberComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('inputCurrency', { static: false })
  public inputElement: ElementRef;
  @Input() public precision: number;
  @Input() public currencySign: string;
  @Input() public defaultValueByClear: number;
  @Input() public autoPaddingZero: boolean = true;
  @Input() public stopPrecisionValidator: boolean = false;
  @Input() public field: IFieldPreferences = {};
  @Input() public type: string;
  @Input() public class: string = '';

  @Input() public numberMultipleOfValidator: (step: number) => ValidatorFn;

  @lazyInject(ScanningService) public scanningService: ScanningService;
  @lazyInject(StateService) public stateService: StateService;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;

  public disabled: boolean;
  public userControl: FormControl = new FormControl();
  public showTrailingZeros: boolean = true;
  public notRoundInitValue: boolean = true;
  public cancelSubscription$ = new Subject<void>();
  public minDecimalPlacesToCheck = DEFAULT_NUMBER_PRECISION;

  protected artificialInputCheck:
    | {
        artificialInputThreshold?: number;
      }
    | boolean = false;

  protected get artificialInputThreshold() {
    return get(
      this.artificialInputCheck,
      'artificialInputThreshold',
      DEFAULT_ARTIFICIAL_INPUT_THRESHOLD,
    );
  }

  public constructor(
    public validationService: ValidationService,
    private countryFormatService: CountryFormatService,
    changeDetectorRef: ChangeDetectorRef,
    public injector: Injector,
  ) {
    super(changeDetectorRef);
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public ngOnInit(): void {
    this.artificialInputCheck = !!get(
      this.field,
      'widgetData.artificialInputCheck',
    );
    if (isNil(this.precision)) {
      this.precision = this.countryFormatService.getCurrencyPrecision();
    }
    if (isNil(this.currencySign)) {
      this.currencySign = this.countryFormatService.getCurrencySymbol();
    }
    this.setValidators();

    super.ngOnInit();
    this.subscriptions.push(
      observableCombineLatest([
        this.control.valueChanges.pipe(rx.startWith(this.control.value)),
        this.divisors$,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ]).subscribe(([inputValue, divisor]: [number, number]) => {
        // null or string should clear the user input
        if (!isFinite(inputValue) && !isNaN(inputValue)) {
          this.userControl.setValue('');
          this.changeDetectorRef.markForCheck();

          return;
        }
        // finite numbers that differs from previously set value should be
        // transferred to the userControl
        if (isFinite(inputValue) && this.disabled) {
          this.lastEmittedValue = inputValue;
          const newValue = this.numberFormatService.getNumberFormatter(
            true,
            undefined,
            merge({}, this.numberFormatService.formatOpts, { symbol: '' }),
          )({
            value: inputValue,
          });
          this.userControl.setValue(newValue);
          this.changeDetectorRef.markForCheck();

          return;
        }
      }),
    );
  }

  public setValidators(): void {
    const validators: ValidatorFn[] = [];
    if (!this.stopPrecisionValidator) {
      validators.push(
        this.validationService.greaterThanDecimalPlacesValidator(this.step),
      );
    }

    const numberMultipleOfValidator = get(this, 'numberMultipleOfValidator');

    if (!isNil(numberMultipleOfValidator)) {
      validators.push(numberMultipleOfValidator(this.step));
    }

    if (this.required) {
      validators.push(this.validationService.requiredValidator);
    }

    if (isNumber(this.max) || has(this.max, '$data') || isFunction(this.max)) {
      validators.push(this.validationService.maxValidator(this.max));
    }

    if (isNumber(this.min) || has(this.min, '$data') || isFunction(this.min)) {
      validators.push(this.validationService.minValidator(this.min));
    }
    this.control.setValidators(validators);
  }

  public onFocusOut(): void {
    const inputDecimalPlaces: number = getDecimalPlaces(this.control.value);
    let currentValue: number | string = this.control.value;

    if (!this.autoPaddingZero) {
      return;
    }
    if (isNil(currentValue) && !isNil(this.defaultValueByClear)) {
      currentValue = this.defaultValueByClear;
    } else if (
      (!isInteger(currentValue) && gt(inputDecimalPlaces, this.precision)) ||
      gt(currentValue, this.max)
    ) {
      return;
    }

    const currentStringValue: string = floatNumberRoundingFormat(currentValue, {
      precision: this.precision,
      thousandsSeparator: DEFAULT_INPUT_THOUSANDS_DIVIDER,
      minDecimalPlacesToCheck: this.minDecimalPlacesToCheck,
    });

    currentValue = removeInvalidZeros(currentStringValue);

    this.userControl.setValue(currentValue);
  }

  public ngAfterViewInit(): void {
    if (this.artificialInputCheck) {
      const artificialInputWatcherOption: IArtificialInputWatcherOption = {
        nativeElement: this.inputElement.nativeElement,
        cancelSubscription$: this.cancelSubscription$,
        artificialInputThreshold: this.artificialInputThreshold,
      };

      artificialInputWatcher(
        EInputEvent.KEYDOWN,
        {
          ...artificialInputWatcherOption,
          updateValueBeforeEventHandler: true,
          scanningService: this.scanningService,
        },
        this.setNewValue.bind(this),
        numberCheckValid,
      );
    }
  }

  public ngOnDestroy(): void {
    this.cancelSubscription$.next();
  }

  public getInputType(): string {
    return this.type ?? (this.disabled ? 'text' : 'number');
  }

  public setNewValue(
    event: KeyboardEvent,
    artificialInputWatcherOption: IArtificialInputWatcherOption,
  ): void {
    const { updateValueBeforeEventHandler } = artificialInputWatcherOption;

    const inputValue = this.inputElement.nativeElement.value;
    const validValue = this.ensureValueAcceptable(inputValue);

    if (validValue !== inputValue) {
      this.inputElement.nativeElement.value = validValue;
    }

    if (
      updateValueBeforeEventHandler &&
      this.lastEmittedValue?.toString() !==
        this.inputElement.nativeElement.value?.toString()
    ) {
      this.setControlValue(
        decimalNumber(this.inputElement.nativeElement.value).valueOf(),
      );
    }
  }

  public ensureValueAcceptable(value: string): string {
    // reset lastIndex for next exec
    const testRegex = generateDecimalRegexp(
      // use large precision(10) to avoid cut off decimals(we need to tip out decimals length error)
      10,
      false,
    );
    const regExpExecuteResult = first(testRegex.exec(value?.toString()));

    return !isEmpty(regExpExecuteResult) ? regExpExecuteResult : null;
  }
}
